import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import './CustomerDatabaseModal.css';
import { apiClient } from '../../../services/apiClient';

const CustomerDatabaseModal = ({ isOpen, onClose, onSave, existingConfig = null }) => {
  const [formData, setFormData] = useState({
    name: 'Customer Database',
    type: 'postgresql',
    host: '',
    port: '',
    database: '',
    username: '',
    password: '',
    ssl: false,
    connectionTimeout: 30,
    maxConnections: 10,
    description: 'Customer information and transaction history'
  });

  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [errors, setErrors] = useState({});

  const databaseTypes = [
    {
      value: 'postgresql',
      label: 'PostgreSQL',
      icon: 'fas fa-database',
      defaultPort: 5432,
      description: 'Advanced open source relational database'
    },
    {
      value: 'mysql',
      label: 'MySQL',
      icon: 'fas fa-database',
      defaultPort: 3306,
      description: 'Popular open source relational database'
    },
    {
      value: 'mongodb',
      label: 'MongoDB',
      icon: 'fas fa-database',
      defaultPort: 27017,
      description: 'Document-oriented NoSQL database'
    },
    {
      value: 'sqlserver',
      label: 'SQL Server',
      icon: 'fas fa-database',
      defaultPort: 1433,
      description: 'Microsoft SQL Server database'
    },
    {
      value: 'oracle',
      label: 'Oracle',
      icon: 'fas fa-database',
      defaultPort: 1521,
      description: 'Enterprise Oracle database'
    },
    {
      value: 'sqlite',
      label: 'SQLite',
      icon: 'fas fa-database',
      defaultPort: '',
      description: 'Lightweight file-based database'
    }
  ];

  useEffect(() => {
    if (existingConfig) {
      setFormData({
        name: existingConfig.name || 'Customer Database',
        type: existingConfig.type || 'postgresql',
        host: existingConfig.host || '',
        port: existingConfig.port || '',
        database: existingConfig.database || '',
        username: existingConfig.username || '',
        password: existingConfig.password || '',
        ssl: existingConfig.ssl || false,
        connectionTimeout: existingConfig.connectionTimeout || 30,
        maxConnections: existingConfig.maxConnections || 10,
        description: existingConfig.description || 'Customer information and transaction history'
      });
    }
  }, [existingConfig]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Auto-fill port when database type changes
    if (field === 'type') {
      const dbType = databaseTypes.find(db => db.value === value);
      if (dbType && dbType.defaultPort) {
        setFormData(prev => ({
          ...prev,
          type: value,
          port: dbType.defaultPort.toString()
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.host.trim()) {
      newErrors.host = 'Host is required';
    }

    if (!formData.port.trim()) {
      newErrors.port = 'Port is required';
    } else if (isNaN(formData.port) || parseInt(formData.port) < 1 || parseInt(formData.port) > 65535) {
      newErrors.port = 'Port must be a valid number between 1 and 65535';
    }

    if (!formData.database.trim()) {
      newErrors.database = 'Database name is required';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await apiClient.testCustomerDatabaseConnection({
          name: formData.name,
          type: formData.type,
          host: formData.host,
          port: parseInt(formData.port),
          database: formData.database,
          username: formData.username,
          password: formData.password,
          ssl: formData.ssl,
          connection_timeout: formData.connectionTimeout,
          max_connections: formData.maxConnections,
          description: formData.description
      });

        setTestResult({
          success: result.success,
          message: result.message,
          details: result.details
        });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Connection failed. Please check your credentials and network connectivity.',
        details: { error: error.message }
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const savedConfig = await apiClient.saveCustomerDatabaseConfig({
          name: formData.name,
          type: formData.type,
          host: formData.host,
          port: parseInt(formData.port),
          database: formData.database,
          username: formData.username,
          password: formData.password,
          ssl: formData.ssl,
          connection_timeout: formData.connectionTimeout,
          max_connections: formData.maxConnections,
          description: formData.description
      });
        
        const config = {
          ...formData,
          id: savedConfig.id || existingConfig?.id || Date.now(),
          type: 'database',
          accessLevel: 'restricted',
          isActive: true,
          integrationStatus: 'configured',
          syncFrequency: 'real-time',
          lastSync: new Date().toISOString(),
          createdAt: existingConfig?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        onSave(config);
        onClose();
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Failed to save configuration',
        details: { error: error.message }
      });
    }
  };

  const selectedDbType = databaseTypes.find(db => db.value === formData.type);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configure Customer Database">
      <div className="customer-database-modal">
        <div className="modal-description">
          <p>Configure your database connection to enable real-time access to information and transaction history.</p>
        </div>

        <form className="database-config-form">
          {/* Left Column - Database Type Selection */}
          <div className="form-left-column">
            <div className="form-group">
              <label htmlFor="db-type">Database Type</label>
              <div className="database-type-selector">
                {databaseTypes.map(dbType => (
                  <div
                    key={dbType.value}
                    className={`database-type-option ${formData.type === dbType.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange('type', dbType.value)}
                  >
                    <div className="db-type-icon">
                      <i className={dbType.icon}></i>
                    </div>
                    <div className="db-type-info">
                      <div className="db-type-name">{dbType.label}</div>
                      <div className="db-type-description">{dbType.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Connection Details */}
          <div className="form-right-column">
            <div className="form-section">
              <h4>Connection Details</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="host">Host</label>
                  <input
                    type="text"
                    id="host"
                    value={formData.host}
                    onChange={(e) => handleInputChange('host', e.target.value)}
                    placeholder="e.g., localhost, 192.168.1.100, db.example.com"
                    className={errors.host ? 'error' : ''}
                  />
                  {errors.host && <span className="error-message">{errors.host}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="port">Port</label>
                  <input
                    type="number"
                    id="port"
                    value={formData.port}
                    onChange={(e) => handleInputChange('port', e.target.value)}
                    placeholder={selectedDbType?.defaultPort ? `Default: ${selectedDbType.defaultPort}` : 'e.g., 3306, 5432, 27017'}
                    className={errors.port ? 'error' : ''}
                  />
                  {errors.port && <span className="error-message">{errors.port}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="database">Database Name</label>
                <input
                  type="text"
                  id="database"
                  value={formData.database}
                  onChange={(e) => handleInputChange('database', e.target.value)}
                  placeholder="e.g., customer_db, myapp_production, analytics"
                  className={errors.database ? 'error' : ''}
                />
                {errors.database && <span className="error-message">{errors.database}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder="e.g., dbuser, admin, readonly_user"
                    className={errors.username ? 'error' : ''}
                  />
                  {errors.username && <span className="error-message">{errors.username}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter your database password"
                    className={errors.password ? 'error' : ''}
                  />
                  {errors.password && <span className="error-message">{errors.password}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Advanced Settings - Full Width */}
          <div className="form-section form-section-full-width">
            <h4>Advanced Settings</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="connection-timeout">Connection Timeout (seconds)</label>
                <input
                  type="number"
                  id="connection-timeout"
                  value={formData.connectionTimeout}
                  onChange={(e) => handleInputChange('connectionTimeout', parseInt(e.target.value))}
                  min="5"
                  max="300"
                />
              </div>

              <div className="form-group">
                <label htmlFor="max-connections">Max Connections</label>
                <input
                  type="number"
                  id="max-connections"
                  value={formData.maxConnections}
                  onChange={(e) => handleInputChange('maxConnections', parseInt(e.target.value))}
                  min="1"
                  max="100"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.ssl}
                    onChange={(e) => handleInputChange('ssl', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Enable SSL/TLS encryption
                </label>
              </div>
            </div>
          </div>

          {/* Test Connection - Full Width */}
          <div className="test-connection-section test-connection-full-width">
            <button
              type="button"
              className="btn btn-secondary test-connection-btn"
              onClick={handleTestConnection}
              disabled={isTesting}
            >
              {isTesting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Testing Connection...
                </>
              ) : (
                <>
                  <i className="fas fa-plug"></i>
                  Test Connection
                </>
              )}
            </button>

            {testResult && (
              <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                <div className="test-result-header">
                  <i className={`fas ${testResult.success ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                  <span>{testResult.message}</span>
                </div>
                {testResult.details && (
                  <div className="test-result-details">
                    {Object.entries(testResult.details).map(([key, value]) => (
                      <div key={key} className="detail-item">
                        <span className="detail-label">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span>
                        <span className="detail-value">{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons - Full Width */}
          <div className="modal-actions modal-actions-full-width">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!testResult?.success}
            >
              <i className="fas fa-save"></i>
              Save Configuration
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CustomerDatabaseModal;
